// src/pages/Resumes.js
//
// Banco de currículos público (Fase G.1.4). Lista apenas currículos com
// `visibility === 'public'` e oferece filtros para empregadores.
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import useProtectedRoute from '../hooks/useProtectedRoute';
import Menu from '../components/Menu';
import Footer from '../components/Footer';
import H1 from '../components/H1/H1';
import ResumesFilterBar from '../components/ResumesFilterBar';
import { CATEGORY_LABELS, AVAILABILITY_TYPES } from '../utils/resumeConstants';

const ResumeCard = ({ resume }) => {
  const categoryLabel = CATEGORY_LABELS[resume.category] || resume.category || '';
  const location = [resume.city, resume.state].filter(Boolean).join(' - ');
  return (
    <div className="bg-white rounded-2xl p-5 border border-gray-200 shadow-sm flex flex-col gap-2">
      <div>
        <h2 className="text-lg font-bold text-purple-refugia">{resume.fullName || 'Currículo'}</h2>
        {resume.headline && <p className="text-sm text-gray-700">{resume.headline}</p>}
      </div>
      <div className="text-xs text-gray-500 flex flex-wrap gap-x-3">
        {categoryLabel && <span className="font-bold text-orange-600 uppercase">{categoryLabel}</span>}
        {location && <span>{location}</span>}
        {resume.nationality && <span>Nacionalidade: {resume.nationality}</span>}
      </div>
      {Array.isArray(resume.skills) && resume.skills.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1">
          {resume.skills.slice(0, 6).map((s) => (
            <span key={s} className="text-xs bg-gray-100 rounded-full px-2 py-0.5">{s}</span>
          ))}
          {resume.skills.length > 6 && <span className="text-xs text-gray-500">+{resume.skills.length - 6}</span>}
        </div>
      )}
      {Array.isArray(resume.availability?.types) && resume.availability.types.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1">
          {resume.availability.types.map((t) => (
            <span key={t} className="text-xs bg-blue-50 text-blue-700 rounded-full px-2 py-0.5">
              {AVAILABILITY_TYPES[t] || t}
            </span>
          ))}
          {resume.availability.remote && <span className="text-xs bg-blue-50 text-blue-700 rounded-full px-2 py-0.5">Remoto</span>}
          {resume.availability.immediate && <span className="text-xs bg-green-50 text-green-700 rounded-full px-2 py-0.5">Imediato</span>}
        </div>
      )}
      <div className="mt-2">
        <Link to={`/curriculos/${resume.id}`} className="text-sm font-bold text-purple-refugia hover:underline">
          Ver currículo completo →
        </Link>
      </div>
    </div>
  );
};

const Resumes = () => {
  const isLoggedIn = useProtectedRoute();
  const [resumes, setResumes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [language, setLanguage] = useState('');
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [availabilityType, setAvailabilityType] = useState('');
  const [remoteOnly, setRemoteOnly] = useState(false);
  const [immediateOnly, setImmediateOnly] = useState(false);

  useEffect(() => {
    const fetchResumes = async () => {
      try {
        const q = query(collection(db, 'resumes'), where('visibility', '==', 'public'));
        const snap = await getDocs(q);
        setResumes(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error('Erro ao carregar currículos:', err);
      } finally {
        setLoading(false);
      }
    };
    if (isLoggedIn) fetchResumes();
  }, [isLoggedIn]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const langTerm = language.trim().toLowerCase();
    const cityTerm = city.trim().toLowerCase();
    return resumes.filter((r) => {
      if (categoryFilter && r.category !== categoryFilter) return false;
      if (state && r.state !== state) return false;
      if (cityTerm && (!r.city || !r.city.toLowerCase().includes(cityTerm))) return false;
      if (availabilityType) {
        const types = Array.isArray(r.availability?.types) ? r.availability.types : [];
        if (!types.includes(availabilityType)) return false;
      }
      if (remoteOnly && !r.availability?.remote) return false;
      if (immediateOnly && !r.availability?.immediate) return false;
      if (langTerm) {
        const langs = Array.isArray(r.languages) ? r.languages : [];
        if (!langs.some((l) => (l.name || '').toLowerCase().includes(langTerm))) return false;
      }
      if (term) {
        const haystack = [
          r.fullName,
          r.headline,
          r.about,
          r.city,
          r.state,
          ...(Array.isArray(r.skills) ? r.skills : []),
          ...(Array.isArray(r.experience) ? r.experience.flatMap((e) => [e.role, e.company]) : []),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      return true;
    });
  }, [resumes, search, categoryFilter, language, state, city, availabilityType, remoteOnly, immediateOnly]);

  const clearFilters = () => {
    setSearch('');
    setCategoryFilter('');
    setLanguage('');
    setState('');
    setCity('');
    setAvailabilityType('');
    setRemoteOnly(false);
    setImmediateOnly(false);
  };

  if (!isLoggedIn) return null;

  return (
    <>
      <Menu isLoggedIn={isLoggedIn} />
      <div className="max-w-7xl mx-auto px-5 py-8">
        <H1 text="Banco de Currículos" bgColor="purple" fontColor="white" />
        <p className="text-sm text-gray-600 mb-5 mt-3">
          Encontre profissionais migrantes e refugiados disponíveis para oportunidades.
        </p>
        <ResumesFilterBar
          search={search}
          onSearchChange={setSearch}
          selectedCategory={categoryFilter}
          onCategoryChange={setCategoryFilter}
          language={language}
          onLanguageChange={setLanguage}
          state={state}
          onStateChange={setState}
          city={city}
          onCityChange={setCity}
          availabilityType={availabilityType}
          onAvailabilityChange={setAvailabilityType}
          remoteOnly={remoteOnly}
          onRemoteToggle={setRemoteOnly}
          immediateOnly={immediateOnly}
          onImmediateToggle={setImmediateOnly}
          onClear={clearFilters}
        />
        {loading ? (
          <p className="text-gray-600">Carregando currículos…</p>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl p-10 text-center border border-gray-200">
            <p className="text-lg font-semibold text-purple-refugia">Nenhum currículo encontrado.</p>
            <p className="text-sm text-gray-500 mt-2">Tente ajustar os filtros.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((r) => <ResumeCard key={r.id} resume={r} />)}
          </div>
        )}
      </div>
      <Footer />
    </>
  );
};

export default Resumes;
